import React, { useEffect, useState } from "react";
import {
    Box, Typography, Paper, Grid, TextField, Button, MenuItem, Select,
    FormControl, InputLabel, Alert, CircularProgress,
    IconButton, Stack,
} from "@mui/material";
import {
    Add as AddIcon, Delete as DeleteIcon, ArrowBack,
    LocalOffer as LocalOfferIcon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { getData, postData, putData, API_ENDPOINTS } from "../../services/apiService";

const TYPE_OPTIONS = [
    { value: "promo_code", label: "Promo Code" },
    { value: "first_booking", label: "First Booking (auto)" },
    { value: "loyalty_tier", label: "Loyalty Tier (auto)" },
    { value: "customer_referral", label: "Customer Referral" },
    { value: "seasonal_route", label: "Seasonal / Route" },
    { value: "trip_milestone", label: "Trip Milestone (DIM-B1)" },
    { value: "capacity_fill", label: "Capacity Fill (DIM-B2)" },
    { value: "rating_reward", label: "Rating Reward (DIM-B3)" },
    { value: "early_delivery", label: "Early Delivery (DIM-B4)" },
    { value: "owner_referral", label: "Owner Referral (DIM-B5)" },
];

const TYPES_NEEDING_DISCOUNT = ["promo_code", "first_booking", "loyalty_tier", "seasonal_route"];
const TYPES_NEEDING_INCENTIVE = ["capacity_fill", "rating_reward", "early_delivery"]; // not trip_milestone (uses rules_json)

const DEFAULT_PAYLOAD = {
    name: "",
    code: "",
    type: "promo_code",
    audience: "customer",
    discount_kind: "flat",
    discount_value: "",
    max_discount_cap: "",
    min_booking_value: "",
    incentive_amount: "",
    budget_cap: "",
    max_per_user: "",
    max_total_uses: "",
    start_at: "",
    expires_at: "",
    rules_json: null,
    promo_codes: [],
};

export default function CampaignBuilder() {
    const navigate = useNavigate();
    const { id } = useParams(); // present when editing a draft
    const isEdit = Boolean(id);

    const [payload, setPayload] = useState(DEFAULT_PAYLOAD);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [errorList, setErrorList] = useState([]);

    // Trip milestone rules — stored as a key-value map locally and serialised
    // into rules_json.milestones on submit.
    const [milestones, setMilestones] = useState([
        { count: 1, amount: 200 },
        { count: 10, amount: 500 },
        { count: 25, amount: 1000 },
        { count: 50, amount: 2000 },
        { count: 100, amount: 5000 },
    ]);

    // Promo codes for promo_code-type campaigns
    const [promoCodes, setPromoCodes] = useState([{ code: "", max_uses: "" }]);

    // seasonal_route route filter (Phase 3)
    const [routeFilter, setRouteFilter] = useState({
        fromCitiesText: "", // comma-separated for v1; multi-select picker is a future polish
        toCitiesText: "",
        maxBookingValue: "",
    });

    useEffect(() => {
        if (!isEdit) return;
        (async () => {
            try {
                const res = await getData(`/api/dim/admin/campaigns/${id}`);
                const c = res?.result;
                if (!c) throw new Error("Campaign not found");
                if (c.status !== "draft") {
                    setError("Only DRAFT campaigns can be edited. Use the lifecycle actions on the detail page instead.");
                    return;
                }
                setPayload({
                    ...DEFAULT_PAYLOAD,
                    name: c.name || "",
                    code: c.code || "",
                    type: c.type,
                    audience: c.audience,
                    discount_kind: c.discount_kind || "",
                    discount_value: c.discount_value ?? "",
                    max_discount_cap: c.max_discount_cap ?? "",
                    min_booking_value: c.min_booking_value ?? "",
                    incentive_amount: c.incentive_amount ?? "",
                    budget_cap: c.budget_cap ?? "",
                    max_per_user: c.max_per_user ?? "",
                    max_total_uses: c.max_total_uses ?? "",
                    start_at: c.start_at ? c.start_at.slice(0, 16) : "",
                    expires_at: c.expires_at ? c.expires_at.slice(0, 16) : "",
                    rules_json: c.rules_json || null,
                });
                if (c.type === "trip_milestone" && c.rules_json?.milestones) {
                    setMilestones(
                        Object.entries(c.rules_json.milestones)
                            .map(([count, amount]) => ({ count: Number(count), amount: Number(amount) }))
                            .sort((a, b) => a.count - b.count)
                    );
                }
                if (c.type === "promo_code" && Array.isArray(c.PromoCodes)) {
                    setPromoCodes(c.PromoCodes.map((pc) => ({ code: pc.code, max_uses: pc.max_uses ?? "" })));
                }
                if (c.type === "seasonal_route" && c.rules_json) {
                    const rf = c.rules_json.route_filter || {};
                    setRouteFilter({
                        fromCitiesText: Array.isArray(rf.from_cities) ? rf.from_cities.join(", ") : "",
                        toCitiesText: Array.isArray(rf.to_cities) ? rf.to_cities.join(", ") : "",
                        maxBookingValue: c.rules_json.max_booking_value ?? "",
                    });
                }
            } catch (e) {
                setError(e.response?.data?.message || e.message || "Failed to load campaign");
            } finally {
                setLoading(false);
            }
        })();
    }, [id, isEdit]);

    const showDiscount = TYPES_NEEDING_DISCOUNT.includes(payload.type);
    const showIncentive = TYPES_NEEDING_INCENTIVE.includes(payload.type);
    const showMilestones = payload.type === "trip_milestone";
    const showPromoCodes = payload.type === "promo_code";
    const showRouteFilter = payload.type === "seasonal_route";

    const setField = (k, v) => {
        setPayload((p) => ({ ...p, [k]: v }));
        setErrorList([]);
        setError(null);
    };

    const buildSubmitPayload = () => {
        const out = { ...payload };
        // Normalise empty strings to null for optional numeric fields the backend expects null on.
        ["discount_value", "max_discount_cap", "min_booking_value", "incentive_amount",
            "max_per_user", "max_total_uses"].forEach((k) => {
                if (out[k] === "" || out[k] === undefined) out[k] = null;
                else if (typeof out[k] === "string") out[k] = Number(out[k]);
            });
        out.budget_cap = Number(out.budget_cap);
        if (!showDiscount) {
            out.discount_kind = null;
            out.discount_value = null;
            out.max_discount_cap = null;
        }
        if (!showIncentive && !showMilestones) {
            out.incentive_amount = null;
        }
        if (showMilestones) {
            const m = {};
            for (const r of milestones) {
                if (r.count > 0 && r.amount > 0) m[String(r.count)] = Number(r.amount);
            }
            out.rules_json = { ...(payload.rules_json || {}), milestones: m };
        }
        if (showPromoCodes) {
            out.promo_codes = promoCodes
                .filter((p) => p.code && p.code.trim().length > 0)
                .map((p) => ({
                    code: p.code.trim().toUpperCase(),
                    max_uses: p.max_uses === "" ? null : Number(p.max_uses),
                }));
        } else {
            delete out.promo_codes;
        }
        if (showRouteFilter) {
            const fromCities = routeFilter.fromCitiesText
                .split(",").map((s) => s.trim()).filter(Boolean);
            const toCities = routeFilter.toCitiesText
                .split(",").map((s) => s.trim()).filter(Boolean);
            out.rules_json = {
                ...(payload.rules_json || {}),
                route_filter: {
                    from_cities: fromCities,
                    to_cities: toCities,
                },
            };
            if (routeFilter.maxBookingValue !== "" && Number(routeFilter.maxBookingValue) > 0) {
                out.rules_json.max_booking_value = Number(routeFilter.maxBookingValue);
            }
        }
        // Datetime-local → ISO
        if (out.start_at && !out.start_at.endsWith("Z")) out.start_at = new Date(out.start_at).toISOString();
        if (out.expires_at && !out.expires_at.endsWith("Z")) out.expires_at = new Date(out.expires_at).toISOString();
        return out;
    };

    const handleSubmit = async () => {
        setSaving(true);
        setError(null);
        setErrorList([]);
        try {
            const body = buildSubmitPayload();
            if (isEdit) {
                await putData(`/api/dim/admin/campaigns/${id}`, body);
                navigate(`/app/campaigns/${id}`);
            } else {
                const res = await postData(API_ENDPOINTS.dimCampaigns, body);
                const newId = res?.result?.campaign_id;
                navigate(newId ? `/app/campaigns/${newId}` : "/app/campaigns");
            }
        } catch (err) {
            const data = err.response?.data;
            setError(data?.message || err.message || "Save failed");
            setErrorList(data?.result?.errors || []);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}><CircularProgress /></Box>;

    return (
        <Box sx={{ p: 3, maxWidth: 1100, mx: "auto", bgcolor: "#F7FAFC", minHeight: "100vh" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                <IconButton onClick={() => navigate("/app/campaigns")}><ArrowBack /></IconButton>
                <Box sx={{
                    width: 44, height: 44, borderRadius: 2,
                    background: "linear-gradient(135deg, #007C84 0%, #016268 55%, #005559 100%)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: "0 3px 10px rgba(0,124,132,0.25)",
                }}>
                    <LocalOfferIcon sx={{ color: "white", fontSize: 22 }} />
                </Box>
                <Box>
                    <Typography variant="h5" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
                        {isEdit ? "Edit Campaign" : "New Campaign"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        DIM-001 · Discounts &amp; Incentives
                    </Typography>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {errorList.length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {errorList.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                </Alert>
            )}

            <Paper elevation={0} sx={{ border: "1px solid #E2E8F0", p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Basics</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth required label="Campaign Name" size="small"
                            value={payload.name} onChange={(e) => setField("name", e.target.value)}
                            inputProps={{ maxLength: 100 }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth required label="Internal Code (slug)" size="small"
                            value={payload.code} onChange={(e) => setField("code", e.target.value)}
                            helperText="Unique identifier — letters, digits, and underscores work well."
                            disabled={isEdit} inputProps={{ maxLength: 64 }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Type</InputLabel>
                            <Select value={payload.type} label="Type" onChange={(e) => setField("type", e.target.value)}>
                                {TYPE_OPTIONS.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Target Audience</InputLabel>
                            <Select value={payload.audience} label="Target Audience" onChange={(e) => setField("audience", e.target.value)}>
                                <MenuItem value="customer">Customer</MenuItem>
                                <MenuItem value="owner">Owner</MenuItem>
                                <MenuItem value="driver">Driver</MenuItem>
                                <MenuItem value="all">All</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>

            {showDiscount && (
                <Paper elevation={0} sx={{ border: "1px solid #E2E8F0", p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>Discount</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Discount Kind</InputLabel>
                                <Select value={payload.discount_kind || ""} label="Discount Kind"
                                    onChange={(e) => setField("discount_kind", e.target.value)}>
                                    <MenuItem value="flat">Flat ₹</MenuItem>
                                    <MenuItem value="percent">Percent (%)</MenuItem>
                                    <MenuItem value="wallet_credit">Wallet Credit</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth label={payload.discount_kind === "percent" ? "Discount %" : "Discount ₹"}
                                size="small" type="number" value={payload.discount_value}
                                onChange={(e) => setField("discount_value", e.target.value)} />
                        </Grid>
                        {payload.discount_kind === "percent" && (
                            <Grid item xs={12} sm={4}>
                                <TextField fullWidth required label="Max Discount Cap (₹)" size="small" type="number"
                                    value={payload.max_discount_cap}
                                    onChange={(e) => setField("max_discount_cap", e.target.value)}
                                    helperText="Per-booking ceiling for percent-type discount." />
                            </Grid>
                        )}
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth label="Min Booking Value (₹)" size="small" type="number"
                                value={payload.min_booking_value}
                                onChange={(e) => setField("min_booking_value", e.target.value)} />
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {showIncentive && (
                <Paper elevation={0} sx={{ border: "1px solid #E2E8F0", p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>Incentive Amount</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth required label="Incentive Amount per Qualifying Event (₹)"
                                size="small" type="number" value={payload.incentive_amount}
                                onChange={(e) => setField("incentive_amount", e.target.value)} />
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {showMilestones && (
                <Paper elevation={0} sx={{ border: "1px solid #E2E8F0", p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>Milestone Bonuses</Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        One row per qualifying trip-count → bonus ₹. Defaults shown.
                    </Typography>
                    <Stack spacing={1}>
                        {milestones.map((m, i) => (
                            <Stack key={i} direction="row" spacing={1} alignItems="center">
                                <TextField label="Trip count" size="small" type="number" value={m.count}
                                    onChange={(e) => {
                                        const v = [...milestones]; v[i] = { ...v[i], count: Number(e.target.value) }; setMilestones(v);
                                    }} />
                                <TextField label="Amount ₹" size="small" type="number" value={m.amount}
                                    onChange={(e) => {
                                        const v = [...milestones]; v[i] = { ...v[i], amount: Number(e.target.value) }; setMilestones(v);
                                    }} />
                                <IconButton size="small" onClick={() => setMilestones(milestones.filter((_, j) => j !== i))}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                        ))}
                        <Button startIcon={<AddIcon />} onClick={() => setMilestones([...milestones, { count: 0, amount: 0 }])}>
                            Add milestone
                        </Button>
                    </Stack>
                </Paper>
            )}

            {showRouteFilter && (
                <Paper elevation={0} sx={{ border: "1px solid #E2E8F0", p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>Route Filter</Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Restrict this campaign to specific routes. Leave a side empty to match any city. City names are matched case-insensitively against the booking's `from_address.city` / `to_address.city`.
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth size="small"
                                label="From cities (comma-separated)"
                                placeholder="Hyderabad, Bangalore"
                                value={routeFilter.fromCitiesText}
                                onChange={(e) => setRouteFilter({ ...routeFilter, fromCitiesText: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth size="small"
                                label="To cities (comma-separated)"
                                placeholder="Mumbai, Pune"
                                value={routeFilter.toCitiesText}
                                onChange={(e) => setRouteFilter({ ...routeFilter, toCitiesText: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth size="small" type="number"
                                label="Max Booking Value (₹, optional)"
                                value={routeFilter.maxBookingValue}
                                onChange={(e) => setRouteFilter({ ...routeFilter, maxBookingValue: e.target.value })}
                                helperText="Bookings above this value are excluded. Leave blank for no upper limit."
                            />
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {showPromoCodes && (
                <Paper elevation={0} sx={{ border: "1px solid #E2E8F0", p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>Promo Codes</Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        One campaign can issue multiple codes (e.g. "DIWALI20", "DIWALI20-VIP") that share its budget.
                    </Typography>
                    <Stack spacing={1}>
                        {promoCodes.map((p, i) => (
                            <Stack key={i} direction="row" spacing={1} alignItems="center">
                                <TextField label="Code" size="small" value={p.code}
                                    onChange={(e) => {
                                        const v = [...promoCodes]; v[i] = { ...v[i], code: e.target.value.toUpperCase() }; setPromoCodes(v);
                                    }} />
                                <TextField label="Max uses (optional)" size="small" type="number" value={p.max_uses}
                                    onChange={(e) => {
                                        const v = [...promoCodes]; v[i] = { ...v[i], max_uses: e.target.value }; setPromoCodes(v);
                                    }} />
                                <IconButton size="small" disabled={promoCodes.length === 1}
                                    onClick={() => setPromoCodes(promoCodes.filter((_, j) => j !== i))}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                        ))}
                        <Button startIcon={<AddIcon />} onClick={() => setPromoCodes([...promoCodes, { code: "", max_uses: "" }])}>
                            Add code
                        </Button>
                    </Stack>
                </Paper>
            )}

            <Paper elevation={0} sx={{ border: "1px solid #E2E8F0", p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Budget & Limits</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth required label="Total Budget Cap (₹)" size="small" type="number"
                            value={payload.budget_cap}
                            onChange={(e) => setField("budget_cap", e.target.value)}
                            helperText="Hard ceiling — campaign auto-pauses when exhausted." />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth label="Max Per User (optional)" size="small" type="number"
                            value={payload.max_per_user}
                            onChange={(e) => setField("max_per_user", e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth label="Max Total Uses (optional)" size="small" type="number"
                            value={payload.max_total_uses}
                            onChange={(e) => setField("max_total_uses", e.target.value)} />
                    </Grid>
                </Grid>
            </Paper>

            <Paper elevation={0} sx={{ border: "1px solid #E2E8F0", p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Schedule</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth required label="Starts at" size="small" type="datetime-local"
                            InputLabelProps={{ shrink: true }} value={payload.start_at}
                            onChange={(e) => setField("start_at", e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth required label="Expires at" size="small" type="datetime-local"
                            InputLabelProps={{ shrink: true }} value={payload.expires_at}
                            onChange={(e) => setField("expires_at", e.target.value)} />
                    </Grid>
                </Grid>
            </Paper>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                <Button onClick={() => navigate("/app/campaigns")}>Cancel</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={saving} sx={{ minWidth: 160, bgcolor: "#007C84", "&:hover": { bgcolor: "#016268" } }}>
                    {saving ? <CircularProgress size={20} /> : isEdit ? "Save Draft" : "Create Draft"}
                </Button>
            </Box>
        </Box>
    );
}
